import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, RouterModule } from '@angular/router';
import { Subject, concatMap, from, firstValueFrom, timeout } from 'rxjs';

interface ConversionItem {
  id?: string;
  file: File;
  status: 'pending' | 'processing' | 'completed' | 'error' | 'cancelled';
  progress: number;
  error?: string;
}

@Component({
  selector: 'app-video-converter',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="app-layout">
      <!-- Navbar -->
      <nav class="navbar glass">
        <div class="logo">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path></svg>
          Sorrus
        </div>
        <div class="nav-links">
          <a routerLink="/launcher" class="back-link">
            <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="none"><polyline points="15 18 9 12 15 6"></polyline></svg>
            Hub
          </a>
          <span class="app-title">Video Konverter</span>
        </div>
        <div class="avatar">YS</div>
      </nav>

      <main class="main-content">
        <div class="hero glass-panel">
          <div class="hero-content">
            <h1>WMV in <span class="text-gradient">MP4 konvertieren.</span></h1>
            <p>Konvertieren Sie Ihre Windows Media Video-Dateien sauber, schnell und verlustfrei in das universelle MP4-Format (H.264 / AAC).</p>
            
            <div class="upload-zone" 
                 [class.dragover]="isDragOver"
                 (dragover)="onDragOver($event)"
                 (dragleave)="onDragLeave($event)"
                 (drop)="onDrop($event)"
                 (click)="fileInput.click()">
              <input type="file" #fileInput (change)="onFilesSelected($event)" multiple accept=".wmv" style="display: none">
              
              <div class="upload-content">
                <div class="icon-circle">
                  <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" stroke-width="2" fill="none"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect><line x1="7" y1="2" x2="7" y2="22"></line><line x1="17" y1="2" x2="17" y2="22"></line><line x1="2" y1="12" x2="22" y2="12"></line><line x1="2" y1="7" x2="7" y2="7"></line><line x1="2" y1="17" x2="7" y2="17"></line><line x1="17" y1="17" x2="22" y2="17"></line><line x1="17" y1="7" x2="22" y2="7"></line></svg>
                </div>
                <h3>WMV-Videos auswählen</h3>
                <p>Klicken oder Videodateien (.wmv) hierher ziehen</p>
              </div>
            </div>
          </div>
          <div class="hero-decoration">
            <div class="glow-orb orb-1"></div>
            <div class="glow-orb orb-2"></div>
          </div>
        </div>

        <!-- Conversion List -->
        <div class="conversion-section" *ngIf="items.length > 0">
          <div class="section-header">
            <h2>Dateien ({{ items.length }})</h2>
            <div class="actions">
              <button class="btn-secondary" (click)="clearAll()">Liste leeren</button>
            </div>
          </div>

          <div class="items-grid">
            <div class="item-card glass-panel" *ngFor="let item of items; let i = index">
              <div class="item-preview">
                <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" stroke-width="2" fill="none"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>
              </div>
              <div class="item-info">
                <span class="filename">{{ item.file.name }}</span>
                <span class="filesize">{{ formatBytes(item.file.size) }}</span>
                
                <div class="status-container">
                  <div class="progress-bar" *ngIf="item.status === 'processing'">
                    <div class="progress-fill" [style.width]="item.progress + '%'"></div>
                  </div>
                  
                  <span class="status-badge" [ngClass]="item.status">
                    <ng-container [ngSwitch]="item.status">
                      <span *ngSwitchCase="'pending'">Wartend in Warteschlange...</span>
                      <span *ngSwitchCase="'processing'">
                        {{ item.progress === 0 ? 'Hochladen & vorbereiten...' : 'Konvertiere... ' + item.progress + '%' }}
                      </span>
                      <span *ngSwitchCase="'completed'">Erfolgreich konvertiert</span>
                      <span *ngSwitchCase="'cancelled'">Abgebrochen</span>
                      <span *ngSwitchCase="'error'">Fehler: {{ item.error }}</span>
                    </ng-container>
                  </span>
                </div>
              </div>
              <div class="item-actions">
                <button class="btn-icon download" *ngIf="item.status === 'completed'" (click)="downloadItem(item)" title="Download MP4">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                </button>
                <button class="btn-icon-danger" *ngIf="item.status === 'processing' || item.status === 'pending'" (click)="cancelItem(item)" title="Abbrechen">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    :host {
      font-family: 'Inter', sans-serif;
      display: block;
      min-height: 100vh;
      background-color: #0f172a;
      color: #f8fafc;
    }

    .app-layout { display: flex; flex-direction: column; min-height: 100vh; }
    
    .glass {
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(12px);
      border-bottom: 1px solid rgba(255,255,255,0.05);
    }

    .glass-panel {
      background: rgba(30, 41, 59, 0.5);
      backdrop-filter: blur(16px);
      border: 1px solid rgba(255,255,255,0.05);
      border-radius: 1.5rem;
    }

    .navbar {
      display: flex; justify-content: space-between; align-items: center;
      padding: 1rem 3rem; position: sticky; top: 0; z-index: 100;
    }

    .logo {
      font-size: 1.25rem; font-weight: 700; display: flex; align-items: center; gap: 0.5rem;
      color: #f8fafc;
    }
    .logo svg { color: #38bdf8; }

    .nav-links { display: flex; gap: 2rem; align-items: center; }
    .nav-links a { color: #94a3b8; text-decoration: none; font-weight: 500; transition: color 0.2s; display: flex; align-items: center; gap: 0.25rem; }
    .nav-links a:hover { color: #38bdf8; }
    .back-link { border-right: 1px solid rgba(255,255,255,0.1); padding-right: 1.5rem; margin-right: 0.5rem; }
    .app-title { font-weight: 600; color: #38bdf8; }

    .avatar {
      width: 40px; height: 40px; border-radius: 50%;
      background: linear-gradient(135deg, #38bdf8, #818cf8);
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; color: white;
    }

    .main-content {
      flex: 1; padding: 3rem; max-width: 1200px; margin: 0 auto; width: 100%; box-sizing: border-box;
      display: flex; flex-direction: column; gap: 3rem;
    }

    .hero {
      position: relative; padding: 4rem; overflow: hidden;
      display: flex; align-items: center; justify-content: center;
    }

    .hero-content {
      position: relative; z-index: 10; max-width: 600px; text-align: center;
    }

    .hero h1 { font-size: 3rem; font-weight: 800; line-height: 1.2; margin: 0 0 1rem; }
    .text-gradient {
      background: linear-gradient(to right, #38bdf8, #818cf8);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    }
    .hero p { color: #94a3b8; font-size: 1.1rem; line-height: 1.6; margin-bottom: 2.5rem; }

    .upload-zone {
      border: 2px dashed rgba(56, 189, 248, 0.3); border-radius: 1rem;
      background: rgba(15, 23, 42, 0.4); transition: all 0.3s ease;
      cursor: pointer; position: relative; overflow: hidden;
    }
    .upload-zone:hover, .upload-zone.dragover {
      border-color: #38bdf8; background: rgba(56, 189, 248, 0.05); transform: translateY(-2px);
    }
    .upload-content { padding: 3rem 2rem; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
    .icon-circle {
      width: 64px; height: 64px; border-radius: 50%; background: rgba(56, 189, 248, 0.1);
      color: #38bdf8; display: flex; align-items: center; justify-content: center;
    }
    .upload-content h3 { margin: 0; color: #f8fafc; font-size: 1.2rem; }
    .upload-content p { margin: 0; color: #64748b; font-size: 0.9rem; }

    .hero-decoration { position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 1; pointer-events: none; overflow: hidden; }
    .glow-orb { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.5; }
    .orb-1 { top: -100px; left: -100px; width: 300px; height: 300px; background: #0ea5e9; }
    .orb-2 { bottom: -100px; right: -100px; width: 400px; height: 400px; background: #4f46e5; }

    .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
    .section-header h2 { font-size: 1.5rem; margin: 0; }

    .btn-secondary {
      background: rgba(255,255,255,0.05); color: #f8fafc; border: 1px solid rgba(255,255,255,0.1);
      padding: 0.75rem 1.5rem; border-radius: 0.75rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
    }
    .btn-secondary:hover { background: rgba(255,255,255,0.1); }

    .items-grid { display: flex; flex-direction: column; gap: 1rem; }
    .item-card {
      display: flex; align-items: center; padding: 1.25rem; gap: 1.5rem;
    }

    .item-preview {
      width: 60px; height: 60px; border-radius: 0.75rem; background: rgba(56, 189, 248, 0.05);
      color: #38bdf8; display: flex; align-items: center; justify-content: center;
    }

    .item-info { flex: 1; display: flex; flex-direction: column; gap: 0.25rem; }
    .filename { font-weight: 600; color: #f1f5f9; }
    .filesize { font-size: 0.85rem; color: #64748b; }

    .status-container { display: flex; align-items: center; gap: 1rem; margin-top: 0.5rem; }
    .progress-bar { flex: 1; height: 6px; background: rgba(255,255,255,0.1); border-radius: 3px; overflow: hidden; max-width: 200px; }
    .progress-fill { height: 100%; background: #38bdf8; transition: width 0.3s; }

    .status-badge { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; padding: 0.2rem 0.6rem; border-radius: 0.5rem; }
    .status-badge.pending { color: #94a3b8; background: rgba(148, 163, 184, 0.1); }
    .status-badge.processing { color: #38bdf8; background: rgba(56, 189, 248, 0.1); }
    .status-badge.completed { color: #34d399; background: rgba(52, 211, 153, 0.1); }
    .status-badge.cancelled { color: #f59e0b; background: rgba(245, 158, 11, 0.1); }
    .status-badge.error { color: #f87171; background: rgba(248, 113, 113, 0.1); }

    .item-actions { display: flex; gap: 0.5rem; }
    
    .btn-icon {
      background: rgba(56, 189, 248, 0.1); color: #38bdf8; border: none; width: 40px; height: 40px;
      border-radius: 0.75rem; cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: all 0.2s;
    }
    .btn-icon:hover { background: #38bdf8; color: #0f172a; }

    .btn-icon-danger {
      background: rgba(248, 113, 113, 0.1); color: #f87171; border: none; width: 40px; height: 40px;
      border-radius: 0.75rem; cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: all 0.2s;
    }
    .btn-icon-danger:hover { background: #f87171; color: #0f172a; }
  `]
})
export class VideoConverterComponent {
  private http = inject(HttpClient);
  items: ConversionItem[] = [];
  isDragOver = false;
  private queue$ = new Subject<ConversionItem>();

  constructor() {
    this.queue$.pipe(
      concatMap(item => from(this.convertItem(item)))
    ).subscribe();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files) {
      this.addFiles(Array.from(files));
    }
  }

  onFilesSelected(event: any) {
    const files = event.target.files;
    if (files) {
      this.addFiles(Array.from(files));
    }
  }

  addFiles(files: File[]) {
    const newItems = files
      .filter(f => f.name.toLowerCase().endsWith('.wmv'))
      .map(file => ({
        file,
        status: 'pending' as const,
        progress: 0
      }));
    
    this.items = [...this.items, ...newItems];
    newItems.forEach(item => this.queue$.next(item));
  }

  async convertItem(item: ConversionItem): Promise<void> {
    if ((item.status as string) === 'cancelled') return;
    
    item.status = 'processing';
    item.progress = 0;
    
    const formData = new FormData();
    formData.append('file', item.file);

    try {
      // 1. Upload the file and receive a taskId
      const uploadRes: any = await firstValueFrom(
        this.http.post(`/api/media/convert-video/upload`, formData)
      );

      item.id = uploadRes.taskId;

      // 2. Poll the status until completed, error, or cancelled
      let isDone = false;
      while (!isDone && (item.status as string) === 'processing') {
        await new Promise(r => setTimeout(r, 1000));
        
        // Skip polling if the user cancelled
        if ((item.status as string) === 'cancelled') {
          break;
        }

        const statusRes: any = await firstValueFrom(
          this.http.get(`/api/media/convert-video/status/${item.id}`)
        );

        if (statusRes.status === 'completed') {
          item.status = 'completed';
          item.progress = 100;
          isDone = true;
        } else if (statusRes.status === 'error') {
          item.status = 'error';
          item.error = statusRes.error || 'Fehler bei der Konvertierung';
          isDone = true;
        } else if (statusRes.status === 'processing') {
          item.progress = statusRes.progress;
        }
      }
    } catch (err: any) {
      console.error('Video conversion error for', item.file.name, err);
      if ((item.status as string) !== 'cancelled') {
        item.status = 'error';
        item.error = err.error?.message || err.message || 'Fehler beim Upload/Verarbeiten';
      }
    }
  }

  cancelItem(item: ConversionItem) {
    if (item.status === 'pending') {
      item.status = 'cancelled';
    } else if (item.status === 'processing') {
      item.status = 'cancelled';
      if (item.id) {
        this.http.post(`/api/media/convert-video/cancel/${item.id}`, {}).subscribe({
          next: () => console.log('Successfully cancelled task', item.id),
          error: (err) => console.error('Error cancelling task', item.id, err)
        });
      }
    }
  }

  downloadItem(item: ConversionItem) {
    if (item.id && item.status === 'completed') {
      window.location.href = `/api/media/convert-video/download/${item.id}`;
    }
  }

  clearAll() {
    // Cancel any active ones
    this.items.forEach(item => {
      if (item.status === 'processing' || item.status === 'pending') {
        this.cancelItem(item);
      }
    });
    this.items = [];
  }

  formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
}
